import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { toast } from 'sonner';
import { useSystemOptions } from '@/contexts/SystemOptionsContext';
import { useIntegration } from '@/providers/IntegrationProvider';
import {
  X,
  AlertCircle,
  Copy,
  MoreVertical,
  Trash2,
  TrendingDown,
  Printer,
  ExternalLink,
} from 'lucide-react';
import { AssetDetailHeader } from '@/components/assets/detail/AssetDetailHeader';
import { AssetDetailOverview } from '@/components/assets/detail/AssetDetailOverview';
import { AssetDetailValues } from '@/components/assets/detail/AssetDetailValues';
import { AssetDetailSupplier } from '@/components/assets/detail/AssetDetailSupplier';
import { AssetDetailEquipment } from '@/components/assets/detail/AssetDetailEquipment';
import { AssetDetailCompliance } from '@/components/assets/detail/AssetDetailCompliance';
import { AssetDetailHistory } from '@/components/assets/detail/AssetDetailHistory';
import { dataService } from '@/services/data-service';
import type { Asset } from '@/types/asset';
import { ASSET_STATUS_LABELS } from '@/types/asset';
import { generateAlertsForAsset, calculateAlertSummary } from '@/services/alerts-service';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { DisposalWizard } from '@/components/assets/disposal-wizard';
import { LabelPrintingModal } from '@/components/assets/label-printing-modal';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAssetPermissions } from '@/hooks/use-permissions';


interface AssetDetailSheetProps {
  readonly assetId: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly defaultTab?: string;
  readonly onAssetUpdated?: () => void;
}

export function AssetDetailSheet({ 
  assetId, 
  open, 
  onOpenChange, 
  defaultTab = 'overview',
  onAssetUpdated 
}: Readonly<AssetDetailSheetProps>) {
  useToast();
  const { options: systemOptions } = useSystemOptions();
  const { settings: integrationSettings } = useIntegration();
  const { canCreate, canEdit, canDelete } = useAssetPermissions();
  
  const [asset, setAsset] = useState<Asset | null>(null);
  const [editedAsset, setEditedAsset] = useState<Asset | null>(null);
  const [originalPhotos, setOriginalPhotos] = useState<Asset['photos']>(undefined);
  const [isEditMode, setIsEditMode] = useState(false);
  const [, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDisposalWizard, setShowDisposalWizard] = useState(false);
  const [showLabelPrintingModal, setShowLabelPrintingModal] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});


  useEffect(() => {
    if (open && assetId) {
      loadAsset();
    }
  }, [assetId, open]);

  useEffect(() => {
    if (defaultTab) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab]);

  useEffect(() => {
    // Clear errors when exiting edit mode
    if (!isEditMode) {
      setErrors({});
    }
  }, [isEditMode]);

  const loadAsset = async () => {
    try {
      setLoading(true);
      const data = await dataService.getAssetById(assetId);
      setAsset(data);
      setEditedAsset(data);
      setOriginalPhotos(data.photos);
    } catch (error) {
      console.error('Failed to load asset:', error);
      toast.error('Failed to load asset details');
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  const validate = (): boolean => {
    if (!editedAsset) return false;
    
    const newErrors: Record<string, string> = {};
    
    // Validate acquisition cost
    validateAcquisitionCost(editedAsset.acquisitionCost, newErrors);

    // Validate financials integration fields if enabled
    const hasFinancialsIntegration = integrationSettings?.enabled && 
      integrationSettings?.integrationType === 'access-financials';
    
    if (hasFinancialsIntegration) {
      validateFinancialsFields(editedAsset, newErrors);
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateAcquisitionCost = (cost: number | undefined | null, errors: Record<string, string>): void => {
    if (cost === undefined || cost === null || cost === 0) {
      errors.acquisitionCost = 'Acquisition value is required and must be greater than 0';
      return;
    }
    if (cost < 0) {
      errors.acquisitionCost = 'Acquisition value cannot be negative';
    }
  };

  const validateFinancialsFields = (asset: Asset, errors: Record<string, string>): void => {
    const requiredNominalFields: Array<{field: keyof Asset; label: string}> = [
      { field: 'assetCostNominal', label: 'Asset Cost Nominal' },
      { field: 'depreciationBSNominal', label: 'Depreciation B/S Nominal' },
      { field: 'depreciationPLNominal', label: 'Depreciation P&L Nominal' },
      { field: 'disposalsNominal', label: 'Disposals Nominal' },
      { field: 'revaluationBSNominal', label: 'Revaluation B/S Nominal' },
      { field: 'revaluationPLNominal', label: 'Revaluation P&L Nominal' },
      { field: 'disposalsBankAccountNominal', label: 'Disposals Bank Account Nominal' },
      { field: 'additionsWIPBankAccountNominal', label: 'Additions WIP / Bank Account Nominal' },
      { field: 'project', label: 'Project' },
      { field: 'costCentre', label: 'Cost Centre' },
    ];

    for (const { field, label } of requiredNominalFields) {
      const value = asset[field];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        errors[field] = `${label} is required`;
      }
    }
  };

  const handleSave = async () => {
    if (!editedAsset) return;

    // Validate before saving
    if (!validate()) {
      // Don't switch tabs - let the user see all tabs with error indicators
      return;
    }

    try {
      setIsSaving(true);
      await dataService.updateAsset(editedAsset);
      setAsset(editedAsset);
      setOriginalPhotos(editedAsset.photos);
      setIsEditMode(false);
      toast.success('Asset updated successfully');
      onAssetUpdated?.();
    } catch (error) {
      console.error('Failed to save asset:', error);
      toast.error('Failed to save asset');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (asset) {
      setEditedAsset({ ...asset, photos: originalPhotos });
    }
    setIsEditMode(false);
  };

  const handleDuplicate = () => {
    if (!asset) return;
    
    window.open(`/assets/new?mode=create`, '_blank');
  };

  const handleDelete = async () => {
    if (!asset) return;

    try {
      await dataService.deleteAsset(asset.id);
      toast.success('Asset deleted successfully');
      onOpenChange(false);
      onAssetUpdated?.();
    } catch (error) {
      console.error('Failed to delete asset:', error);
      toast.error('Failed to delete asset');
    }
  };

  const handleDisposal = () => {
    setShowDisposalWizard(true);
  };

  const handleDisposalComplete = () => {
    setShowDisposalWizard(false);
    loadAsset();
    onAssetUpdated?.();
  };

  const handlePrintLabel = () => {
    setShowLabelPrintingModal(true);
  };

  const handleOpenInNewTab = () => {
    window.open(`/assets/${assetId}`, '_blank');
  };



  const alerts = useMemo(() => {
    if (!asset) return [];
    return generateAlertsForAsset(asset);
  }, [asset]);

  const alertSummary = useMemo(() => {
    return calculateAlertSummary(alerts);
  }, [alerts]);

  const hasChanges = useMemo(() => {
    if (!asset || !editedAsset) return false;
    return JSON.stringify(asset) !== JSON.stringify(editedAsset);
  }, [asset, editedAsset]);

  const renderSheetContent = () => {
    if (loading) {
      return (
        <div className="flex-1 p-6 space-y-4">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      );
    }

    if (asset && editedAsset) {
      return (
        <ScrollArea className="flex-1">
          <div className="p-6">
            <AssetDetailHeader
              asset={editedAsset}
              onAssetUpdate={setEditedAsset}
              isEditMode={isEditMode}
            />

            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6" data-testid="asset-detail-tabs">
              <TabsList className="grid w-full grid-cols-6">
                <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                <TabsTrigger value="values" data-testid="tab-values">Values</TabsTrigger>
                <TabsTrigger value="supplier" data-testid="tab-supplier">Supplier</TabsTrigger>
                <TabsTrigger value="equipment" data-testid="tab-equipment">Equipment</TabsTrigger>
                <TabsTrigger value="compliance" data-testid="tab-compliance">Compliance</TabsTrigger>
                <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-6">
                <AssetDetailOverview
                  asset={editedAsset}
                  onAssetUpdate={setEditedAsset}
                  isEditMode={isEditMode}
                  systemOptions={systemOptions}
                />
              </TabsContent>

              <TabsContent value="values" className="mt-6">
                <AssetDetailValues
                  asset={editedAsset}
                  onAssetUpdate={setEditedAsset}
                  isEditMode={isEditMode}
                  errors={errors}
                />
              </TabsContent>

              <TabsContent value="supplier" className="mt-6">
                <AssetDetailSupplier
                  asset={editedAsset}
                  onAssetUpdate={setEditedAsset}
                  isEditMode={isEditMode}
                />
              </TabsContent>

              <TabsContent value="equipment" className="mt-6">
                <AssetDetailEquipment
                  asset={editedAsset}
                  onAssetUpdate={setEditedAsset}
                  isEditMode={isEditMode}
                />
              </TabsContent>

              <TabsContent value="compliance" className="mt-6">
                <AssetDetailCompliance
                  asset={editedAsset}
                  onAssetUpdate={setEditedAsset}
                  isEditMode={isEditMode}
                />
              </TabsContent>

              <TabsContent value="history" className="mt-6">
                <AssetDetailHistory asset={asset} />
              </TabsContent>
            </Tabs>
          </div>
        </ScrollArea>
      );
    }

    return null;
  };

  if (!open) return null;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-[800px] p-0 flex flex-col" data-testid="asset-detail-panel">
          <SheetHeader className="px-6 py-4 border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <SheetTitle className="text-xl">
                  {loading && 'Loading...'}
                  {!loading && (asset?.name ?? 'Asset Details')}
                </SheetTitle>
                {asset && (
                  <Badge variant={asset.status === 'active' ? 'default' : 'secondary'}>
                    {ASSET_STATUS_LABELS[asset.status]}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {alertSummary.totalCount > 0 && (
                  <Badge variant="destructive" className="gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {alertSummary.totalCount}
                  </Badge>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleOpenInNewTab}>
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Open in New Tab
                    </DropdownMenuItem>
                    {canCreate && (
                      <DropdownMenuItem onClick={handleDuplicate}>
                        <Copy className="mr-2 h-4 w-4" />
                        Duplicate Asset
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={handlePrintLabel}>
                      <Printer className="mr-2 h-4 w-4" />
                      Print Label
                    </DropdownMenuItem>
                    {(canEdit || canDelete) && <DropdownMenuSeparator />}
                    {canEdit && (
                      <DropdownMenuItem onClick={handleDisposal}>
                        <TrendingDown className="mr-2 h-4 w-4" />
                        Dispose Asset
                      </DropdownMenuItem>
                    )}
                    {canDelete && (
                      <DropdownMenuItem
                        onClick={() => setShowDeleteDialog(true)}
                        className="text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete Asset
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onOpenChange(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </SheetHeader>

          {renderSheetContent()}
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent data-testid="delete-asset-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Asset</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this asset? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground" data-testid="delete-confirm-button">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Disposal Wizard */}
      {asset && (
        <DisposalWizard
          asset={asset}
          open={showDisposalWizard}
          onClose={() => setShowDisposalWizard(false)}
          onComplete={handleDisposalComplete}
        />
      )}

      {/* Label Printing Modal */}
      {asset && (
        <LabelPrintingModal
          open={showLabelPrintingModal}
          onClose={() => setShowLabelPrintingModal(false)}
          assets={[asset]}
        />
      )}

    </>
  );
}
